/**
 * hosted-code-client.ts — the CLI's transport for G2 E2EE code sharing.
 *
 * Talks to the hosted `POST /api/sync/code` bridge (bearer-authed) for the ciphertext
 * store + the public-key directory, and drives the client-side crypto in `code-share.ts`
 * on top. It is BOTH:
 *   - a `CodeShareTransport` (the 4 low-level ciphertext/public-key calls), and
 *   - the keystore-bearing implementation of the high-level `shareCodePlain` /
 *     `fetchCodePlain` the MCP tools call.
 *
 * Why the hosted rail (not the local room server): wraps are keyed on the REAL
 * room_members uuid so a teammate on ANOTHER machine finds their wrap. The local
 * server mints synthetic per-process ids that never match across machines, so it can't
 * carry cross-machine code sharing. The server here only ever sees ciphertext + public
 * keys; all encryption/decryption happens in this process against `~/.aethereum/keys/`.
 *
 * The caller's own member uuid is learned from the `publish_key` response (the only
 * cross-machine-consistent identity) and cached, so the client can name itself the same
 * way the directory does when it wraps and when it looks for its own wrap on fetch.
 */

import { homedir } from "node:os";
import { e2ee, type HiveClient } from "@br9704/aethereum-shared";
import { resolveProject } from "./config-store.js";
import { loadVerifyStore } from "./verify-store.js";
import {
  provisionAndPublish,
  shareCode as orchestrateShare,
  fetchCode as orchestrateFetch,
  type CodeShareTransport,
  type RosterEntry,
} from "./code-share.js";

interface CodeRow {
  id: string;
  refIndex: string;
  version: number;
  keyEpoch: number;
  alg: string;
  ciphertext: string;
  nonce: string;
  wrappedKeys: e2ee.WrappedKeyWire[];
  authorSig: string;
  contract?: string;
  authorMember: string;
}

export class HostedCodeClient implements CodeShareTransport {
  private memberId: string | null = null;
  /** v6-S1: room policy piggybacked on the last member_keys read. null = never read. */
  private requireVerifiedFlag: boolean | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
    private readonly roomId: string,
    private readonly home: string = homedir(),
    private readonly fetchImpl: typeof fetch = fetch,
  ) {}

  private async call(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await this.fetchImpl(`${this.baseUrl}/api/sync/code`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${this.token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`aethereum code ${String(body.action)} failed (${res.status})`);
    }
    const json = (await res.json()) as Record<string, unknown>;
    if (json.ok === false && typeof json.notice === "string") {
      // Daily-cap sentinel: informational, surfaced to the agent, never a throw-crash.
      throw new Error(json.notice);
    }
    return json;
  }

  // ── CodeShareTransport (low-level ciphertext / public-key calls) ──────────────

  async shareCode(input: {
    refIndex: string;
    version: number;
    keyEpoch: number;
    alg: string;
    ciphertext: string;
    nonce: string;
    wrappedKeys: e2ee.WrappedKeyWire[];
    authorSig: string;
    size: number;
    contract?: string;
    idempotency: string;
  }): Promise<{ id: string }> {
    const r = await this.call({ action: "share", ...input });
    return { id: String(r.id) };
  }

  async fetchCode(selector: { id?: string; refIndex?: string }): Promise<CodeRow | null> {
    const r = await this.call({ action: "fetch", ...selector });
    return (r.row as CodeRow | null) ?? null;
  }

  async publishPublicKey(input: {
    identityPub: string;
    wrapPub: string;
    wrapPubSig: string;
    alg: string;
  }): Promise<void> {
    const r = await this.call({ action: "publish_key", ...input });
    if (typeof r.member === "string") this.memberId = r.member;
  }

  async memberPublicKeys(): Promise<RosterEntry[]> {
    const r = await this.call({ action: "member_keys" });
    // v6-S1: cache the room's require-verified policy off the same read. An older
    // server omits the field => treated as off (fail-soft; client-enforced policy).
    this.requireVerifiedFlag = typeof r.requireVerified === "boolean" ? r.requireVerified : false;
    return (r.members as RosterEntry[] | undefined) ?? [];
  }

  /**
   * The room's require-verified E2EE policy. Reads the directory if never read;
   * any failure degrades to `{ requireVerified: false, degraded: true }` so a
   * policy-read outage can never block a share (fail-soft, but reportable).
   */
  async shareSettings(): Promise<{ requireVerified: boolean; degraded: boolean }> {
    if (this.requireVerifiedFlag !== null) {
      return { requireVerified: this.requireVerifiedFlag, degraded: false };
    }
    try {
      await this.memberPublicKeys();
      return { requireVerified: this.requireVerifiedFlag ?? false, degraded: false };
    } catch {
      return { requireVerified: false, degraded: true };
    }
  }

  // ── high-level, keystore-bearing surface (what the MCP tools call) ────────────

  /**
   * Resolve THIS member's directory uuid, provisioning the keystore + publishing the
   * signed public key on first use. The uuid comes back from the server (it resolves
   * the bearer token), so the CLI names itself exactly as the directory does.
   */
  private async whoami(): Promise<string> {
    if (this.memberId) return this.memberId;
    // A cosmetic local label; the real identity is the uuid the publish call returns.
    await provisionAndPublish(this, this.roomId, "self", this.home);
    if (!this.memberId) throw new Error("could not resolve member id from the code directory");
    return this.memberId;
  }

  async shareCodePlain(input: {
    ref: string;
    plaintext: string;
    contract?: string;
    /** v6-S1: informed-consent hook for first-contact recipients (CLI TTY path). */
    onFirstContact?: (
      recipients: Array<{ member: string; identityPub: string }>,
    ) => Promise<boolean> | boolean;
  }): Promise<{
    id: string;
    recipients: number;
    dropped: number;
    excluded: string[];
    firstContact: string[];
    policyDegraded: boolean;
  }> {
    const me = await this.whoami();
    // Load pinned identities so shareCode can hard-block on a post-verification key
    // substitution (F-1), plus the verified set + the room's require-verified policy
    // (v6-S1). No pins yet => TOFU share, surfaced via firstContact.
    const store = loadVerifyStore(this.roomId, this.home);
    const policy = await this.shareSettings();
    const res = await orchestrateShare(this, {
      roomId: this.roomId,
      member: me,
      ref: input.ref,
      plaintext: input.plaintext,
      contract: input.contract,
      pins: store.pins,
      verified: store.verified,
      requireVerified: policy.requireVerified,
      onFirstContact: input.onFirstContact,
      home: this.home,
    });
    return {
      id: res.id,
      recipients: res.recipients,
      dropped: res.dropped,
      excluded: res.excluded,
      firstContact: res.firstContact,
      policyDegraded: policy.degraded,
    };
  }

  async fetchCodePlain(input: {
    ref: string;
    id?: string;
  }): Promise<{ plaintext: string; version: number; contract?: string; authorMember: string; authorVerified: boolean } | null> {
    const me = await this.whoami();
    const got = await orchestrateFetch(this, {
      roomId: this.roomId,
      member: me,
      ref: input.ref,
      id: input.id,
      home: this.home,
    });
    if (!got) return null;
    return {
      plaintext: new TextDecoder().decode(got.plaintext),
      version: got.version,
      contract: got.contract,
      authorMember: got.authorMember,
      authorVerified: got.authorVerified,
    };
  }

  /** Provision + publish this member's keys without sharing anything (for init/join). */
  async provision(): Promise<string> {
    return this.whoami();
  }
}

/**
 * Attach the high-level code-sharing methods to any `HiveClient`, backed by the hosted
 * code rail resolved from the project config (url + token + roomId). This is how the
 * MCP `share_code` / `fetch_code` tools reach the client-side crypto in the runtime:
 * the rest of the client stays the local room-server transport, but code goes over the
 * hosted rail (real uuids). Fail-soft: with no room config the methods throw a clear
 * message the MCP handler turns into a note. The CALLER gates activation (the runtime
 * only calls this when AETHEREUM_CODE_SHARING is set), so it never turns on unasked.
 */
export function attachCodeSharing(
  client: HiveClient,
  cwd: string = process.cwd(),
  home: string = homedir(),
): HiveClient {
  const make = (): HostedCodeClient => {
    const proj = resolveProject(cwd, home);
    if (!proj?.url || !proj?.token || !proj?.roomId) {
      throw new Error(
        "no room is configured here for code sharing (run `aethereum join <code>` or `aethereum init` first)",
      );
    }
    return new HostedCodeClient(proj.url, proj.token, proj.roomId, home);
  };
  client.shareCodePlain = (input) => make().shareCodePlain(input);
  client.fetchCodePlain = (input) => make().fetchCodePlain(input);
  return client;
}
